import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight, Copy, FolderKanban, Link2, Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import { aaiApi } from '../../services/aaiApi';
import { createProviderClient, getProjectsRoster, getProviderClientLink } from '../../services/api';
import { useAsync } from '../aai/useAsync';
import type { PartnerClientRow, PartnerOverview } from '../../types/aai';
import {
  Action, Chip, EmptyState, ErrorNote, Field, Input, Loading, Modal, PageBody, PageHead, Panel, StatusChip,
  HelpToggle,
} from '../../components/aa';
import { STATUS_CHIP, STATUS_EXPLAINED, STATUS_LABEL, relativeTime } from '../partner/format';
// Scoping plugin panels hidden for provider owners for now.
// import { ScopingPluginPanels } from '../partner/ScopingPluginSection';

/*
 * Clients: the companies a provider company serves. Only its Owner reaches
 * this page. A client added here runs the Agents Anywhere framework,
 * is served by this provider's experts, and its owner is invited to set up
 * their company.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/*
 * The other way in, the same as a Solution Partner's client link: the client
 * sets up their own company with Orbit, and it lands on this list.
 */
const ClientLink: React.FC = () => {
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const prepare = async () => {
    setBusy(true);
    try {
      const url = link || (await getProviderClientLink()).url;
      setLink(url);
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Client link copied. It works for 30 days.');
      } catch {
        toast.info('The link is ready below. Select it and copy it yourself.');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Could not prepare the client link');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 border-t border-line-soft pt-5">
      <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft">Or share your client link</p>
      <p className="m-0 mt-1.5 text-[13px] leading-[20px] text-ink-muted">
        The client sets up their own company and onboards with Orbit, and they are added to your clients.
      </p>
      {link && <p className="m-0 mt-2 break-all rounded-act bg-canvas-inset px-3 py-2 font-sans text-[12px] text-ink">{link}</p>}
      <div className="mt-3">
        <Action variant="quiet" icon={link ? <Copy size={14} /> : <Link2 size={14} />} onClick={prepare} disabled={busy} reason="Preparing the link">
          {link ? 'Copy client link' : 'Prepare client link'}
        </Action>
      </div>
    </div>
  );
};

export const AddClientModal: React.FC<{ onClose: () => void; onAdded: (id: string) => void }> = ({ onClose, onAdded }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [contact, setContact] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [invite, setInvite] = useState(true);
  const [busy, setBusy] = useState(false);

  let reason = '';
  if (!EMAIL.test(email.trim())) reason = 'Enter the owner\'s email address';
  if (!name.trim()) reason = 'Enter the company name';
  if (busy) reason = 'Adding the client';

  const add = async () => {
    setBusy(true);
    try {
      const res = await createProviderClient({
        name: name.trim(), email: email.trim(), contact_name: contact.trim() || undefined,
        phone: phone.trim() || undefined, website: website.trim() || undefined, send_invite: invite,
      });
      if (res.invite?.sent) toast.success(`${name.trim()} added. The invite went to ${res.invite.email}.`);
      if (!res.invite?.sent && invite) toast.warn(`${name.trim()} added, but the invite was not sent. Send it from the client.`);
      if (!invite) toast.success(`${name.trim()} added.`);
      onAdded(res.client_id);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Could not add the client');
      setBusy(false);
    }
  };

  return (
    <Modal
      size="dialog"
      context="Add client"
      onClose={onClose}
      footer={(
        <div className="flex justify-end gap-2">
          <Action variant="ghost" onClick={onClose}>Cancel</Action>
          <Action icon={<Plus size={14} />} onClick={add} disabled={!!reason} reason={reason}>Add client</Action>
        </div>
      )}
    >
      <h2 className="m-0 text-[22px] font-bold leading-[29px] tracking-[-0.015em] text-ink">Add a client</h2>
      <p className="m-0 mt-2 text-[13.5px] leading-[22px] text-ink-muted">
        The client gets its own company and workspace. Your experts build for it, and you can open it from this list.
      </p>
      <div className="mt-5 space-y-4">
        <Field label="Company name" htmlFor="pc-name">
          <Input id="pc-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Owner's email" htmlFor="pc-email" hint="The person who runs the company. They set up their company from the invite.">
          <Input id="pc-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Owner's name" htmlFor="pc-contact">
          <Input id="pc-contact" value={contact} onChange={(e) => setContact(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Owner's phone" htmlFor="pc-phone" hint="Optional.">
          <Input id="pc-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Website" htmlFor="pc-website" hint="Optional. Orbit reads it so the owner starts with facts to confirm.">
          <Input id="pc-website" value={website} onChange={(e) => setWebsite(e.target.value)} autoComplete="off" />
        </Field>
        <label className="flex items-center gap-2 text-[13px] text-ink">
          <input type="checkbox" checked={invite} onChange={(e) => setInvite(e.target.checked)} />
          Email the owner their invite now
        </label>
      </div>
      <ClientLink />
    </Modal>
  );
};

/* Each client's builds and the experts on them, from the same roster the
   Projects page reads, so both screens count the same work. */
type ClientWork = { projects: number; experts: string[] };

const workByClient = (rows: any[]): Record<string, ClientWork> => {
  const out: Record<string, ClientWork> = {};
  for (const r of rows) {
    const id = String(r.client_id || r.workspace_id || '');
    if (!id) continue;
    const work = out[id] || { projects: 0, experts: [] };
    work.projects += 1;
    const name = r.expert?.name;
    if (name && !work.experts.includes(name)) work.experts.push(name);
    out[id] = work;
  }
  return out;
};

const expertsLabel = (experts: string[]): string => {
  if (experts.length === 0) return 'Not assigned yet';
  if (experts.length <= 2) return experts.join(', ');
  return `${experts.slice(0, 2).join(', ')} +${experts.length - 2}`;
};

const ClientRow: React.FC<{ row: PartnerClientRow; work?: ClientWork; striped: boolean; onOpen: () => void }> = ({ row, work, striped, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className={clsx('flex w-full items-center gap-4 px-6 py-3.5 text-left text-[13px] transition-colors hover:bg-canvas', striped && 'bg-card')}
  >
    <div className="flex w-[200px] shrink-0 items-center gap-2">
      <span className="truncate font-semibold text-ink">{row.name}</span>
      {row.retainer && <Chip tone="private">Retainer</Chip>}
    </div>
    <div className="w-[180px] shrink-0 truncate text-ink-muted">{row.contact_name || row.contact_email || 'Not yet named'}</div>
    <div className="w-[120px] shrink-0"><StatusChip status={STATUS_CHIP[row.status]} label={STATUS_LABEL[row.status]} /></div>
    <div className="w-[110px] shrink-0">
      <div className="tabular-nums text-ink">{work?.projects ?? 0}</div>
      {row.projects_open > 0 && <div className="text-[11.5px] text-ink-dim">{row.projects_open} open {row.projects_open === 1 ? 'request' : 'requests'}</div>}
    </div>
    <div className={clsx('w-[170px] shrink-0 truncate', work?.experts.length ? 'text-ink' : 'text-ink-dim')}>{expertsLabel(work?.experts || [])}</div>
    <div className="min-w-0 flex-1 truncate text-[12.5px] text-ink-dim">
      {row.last_activity ? `${row.last_activity.label} · ${relativeTime(row.last_activity.at)}` : 'No activity yet'}
    </div>
    <ArrowRight size={16} className="shrink-0 text-ink-soft" />
  </button>
);

const ProviderClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const overview = useAsync<PartnerOverview>(() => aaiApi.getPartnerOverview(), 'Could not load your clients.', []);
  const roster = useAsync<any>(() => getProjectsRoster().then((data) => ({ data })).catch(() => ({ data: { client_work: [] } })), '', []);
  const work = workByClient(roster.data?.client_work || []);

  if (!localStorage.getItem('provider_id')) return <Navigate to="/dashboard" replace />;

  const rows = overview.data?.clients || [];

  return (
    <PageBody>
      <PageHead
        title="Clients"
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            <Action variant="quiet" icon={<FolderKanban size={14} />} to="/clients/projects">Client projects</Action>
            <Action icon={<Plus size={14} />} onClick={() => setAdding(true)}>Add client</Action>
          </div>
        )}
      />
      {overview.loading && <Loading label="Loading your clients" />}
      {overview.error && <ErrorNote message={overview.error} />}
      {!overview.loading && !overview.error && (
        <div className="space-y-5">
          <Panel>
            {rows.length > 0 && (
              <div className="overflow-x-auto">
                <div className="min-w-[960px]">
                  <div className="flex items-center gap-4 border-b border-line-soft px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">
                    <div className="w-[200px] shrink-0">Client</div>
                    <div className="w-[180px] shrink-0">Owner</div>
                    <div className="w-[120px] shrink-0">Status</div>
                    <div className="w-[110px] shrink-0">Projects</div>
                    <div className="w-[170px] shrink-0">Experts</div>
                    <div className="min-w-0 flex-1">Last activity</div>
                    <span className="w-4 shrink-0" />
                  </div>
                  {rows.map((row, i) => (
                    <ClientRow key={row.id} row={row} work={work[row.id]} striped={i % 2 === 1} onOpen={() => navigate(`/clients/${row.id}`)} />
                  ))}
                </div>
              </div>
            )}
            {rows.length === 0 && (
              <EmptyState
                title="No clients yet"
                reason="Add a client to give them their own company and workspace. Their owner gets an invite to set it up."
                action={<Action onClick={() => setAdding(true)}>Add client</Action>}
              />
            )}
          </Panel>
          <HelpToggle label="What the statuses mean" className="px-1">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-[12px] text-ink-dim">
              {(['draft', 'invited', 'onboarding', 'active'] as const).map((status) => (
                <span key={status}><b className="text-ink">{STATUS_LABEL[status]}</b>: {STATUS_EXPLAINED[status]}</span>
              ))}
            </div>
          </HelpToggle>
          {/* <ScopingPluginPanels clients={rows} /> */}
        </div>
      )}
      {adding && (
        <AddClientModal
          onClose={() => setAdding(false)}
          onAdded={(id) => { setAdding(false); navigate(`/clients/${id}`); }}
        />
      )}
    </PageBody>
  );
};

export default ProviderClientsPage;
